/* Root app: routes, state, recalc orchestration.
 *
 * On stage 7b the mock has been removed: every state change that produced
 * a "vilka" before now goes through the backend.
 *
 *   - Bootstrap: ping /health, fall back to a full-screen gate on failure;
 *     then ensure we have a JWT (anonymous if needed) and load catalogs.
 *   - Recalculate: POST /resume/calculate { form } -> Calculation + Recs + Resume.
 *   - Versions: GET /versions / POST /versions / DELETE /versions/{id}.
 *
 * localStorage keeps only UX flags: the JWT (under 'vilka-token'), the
 * splash-seen flag, and the draft form (so a refresh before "Recalculate"
 * doesn't lose typing). Everything else is reloaded from the backend.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import * as authApi from './api/auth.js';
import { catalogs as catalogsApi } from './api/catalogs.js';
import { ApiError, getToken, pingHealth } from './api/client.js';
import { resume as resumeApi } from './api/resume.js';
import { versions as versionsApi } from './api/versions.js';
import { BackendGate } from './components/BackendGate.jsx';
import { Toast } from './components.jsx';
import { emptyForm, sampleForm } from './data.js';
import { HistoryDrawer } from './history.jsx';
import { Landing } from './landing.jsx';
import { Splash } from './splash.jsx';
import { SplitView } from './splitview.jsx';

const DRAFT_KEY = 'vilka-draft-v2';

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function saveDraft(form) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
}

/* Mapping helpers between backend DTOs and the shapes UI components expect. */

function calcToResult(calc) {
  return {
    id: calc.id,
    min: calc.min_amount,
    max: calc.max_amount,
    mid: calc.mid_amount,
    marketPercentile: calc.market_percentile,
    predictorVersion: calc.predictor_version,
  };
}

function recToRow(rec) {
  return {
    id: rec.id,
    slug: rec.slug,
    cat: rec.category,
    icon: rec.icon,
    title: rec.title,
    sub: rec.sub,
    effect: rec.effect,
    field: rec.applied_field,
    payload: rec.applied_payload,
    // Skill recs carry weights for both ranking modes; rule-based recs carry
    // null on both. The ResultPanel uses these to re-rank when the user flips
    // the "popularity / cost" mode switch and to filter out picked skills.
    popularityRelevance: rec.popularity_relevance,
    costRelevance: rec.cost_relevance,
  };
}

function versionToCard(v) {
  return {
    id: v.id,
    createdAt: new Date(v.created_at).getTime(),
    form: v.form,
    result: {
      min: v.calculation.min_amount,
      max: v.calculation.max_amount,
      mid: v.calculation.mid_amount,
      predictorVersion: v.calculation.predictor_version,
    },
    deltaMid: v.delta_mid_to_prev,
  };
}


export function App() {
  // ---- bootstrap state ----
  const [bootState, setBootState] = useState('checking'); // checking | ready | gated
  const [me, setMe] = useState(null);

  // ---- catalogs ----
  const [catalogs, setCatalogs] = useState(null);

  // ---- main UI state ----
  const [screen, setScreen] = useState('landing');
  const [form, setForm] = useState(() => loadDraft() || emptyForm());
  const [lastSubmittedForm, setLastSubmittedForm] = useState(null);
  const [result, setResult] = useState(null);
  const [prevResult, setPrevResult] = useState(null);
  const [currentCalcId, setCurrentCalcId] = useState(null);
  const [recs, setRecs] = useState([]);
  const [versions, setVersions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeRecId, setActiveRecId] = useState(null);
  const [fieldGlow, setFieldGlow] = useState(null);
  const [pulseToken, setPulseToken] = useState(0);
  const [toast, setToast] = useState({ visible: false, message: '' });

  // Splash now plays on every boot/refresh (no localStorage flag) — it's
  // short (~1s) and reinforces the brand mark every time the app loads.
  const [splashShowing, setSplashShowing] = useState(true);
  const dismissSplash = () => setSplashShowing(false);

  const [headerAnim, setHeaderAnim] = useState('hover');
  const [headerAnimTrigger, setHeaderAnimTrigger] = useState(0);

  useEffect(() => { saveDraft(form); }, [form]);

  useEffect(() => {
    const onDown = () => setBootState('gated');
    window.addEventListener('vilka:backend-unreachable', onDown);
    return () => window.removeEventListener('vilka:backend-unreachable', onDown);
  }, []);

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    boot();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function boot() {
    setBootState('checking');
    try {
      await pingHealth();
    } catch {
      setBootState('gated');
      return;
    }
    try {
      if (!getToken()) await authApi.anon();
      const m = await authApi.me();
      setMe(m);
    } catch {
      // Stale token? Try once more with a fresh anon.
      try {
        await authApi.anon();
        setMe(await authApi.me());
      } catch { setBootState('gated'); return; }
    }
    try {
      const [roles, regions, skills, experience, languages, languageLevels, employment] = await Promise.all([
        catalogsApi.roles(),
        catalogsApi.regions(),
        catalogsApi.skills(undefined, undefined, 2000),
        catalogsApi.experience(),
        catalogsApi.languages(),
        catalogsApi.languageLevels(),
        catalogsApi.employment(),
      ]);
      setCatalogs({ roles, regions, skills, experience, languages, languageLevels, employment });
    } catch { setBootState('gated'); return; }
    try {
      const vs = await versionsApi.list();
      setVersions(vs.map(versionToCard));
    } catch { /* non-fatal */ }
    setBootState('ready');
  }

  const completion = useMemo(() => calcCompletion(form), [form]);
  const isEmpty = !result;
  const dirty = !!lastSubmittedForm && JSON.stringify(lastSubmittedForm) !== JSON.stringify(form);

  const goSplit = (arg) => {
    // arg can be:
    //   - true              -> fill with sampleForm() (internal "Заполнить примером")
    //   - false / undefined -> empty form, no change
    //   - a form object     -> prefill with that exact form (hero carousel click)
    if (arg === true) setForm(sampleForm());
    else if (arg && typeof arg === 'object') setForm(arg);
    setScreen('split');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };
  const goHome = () => {
    setScreen('landing');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const showToast = (message) => {
    setToast({ visible: true, message });
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast({ visible: false, message: '' }), 3000);
  };

  async function recalc() {
    setError(null);
    setIsLoading(true);
    setHeaderAnim('pulse');
    const formSnapshot = { ...form, skills: [...form.skills] };
    try {
      const r = await resumeApi.calculate(formSnapshot);
      const newResult = calcToResult(r.calculation);
      setPrevResult(result);
      setResult(newResult);
      setCurrentCalcId(r.calculation.id);
      setRecs(r.recommendations.map(recToRow));
      setLastSubmittedForm(formSnapshot);
      setPulseToken(Date.now());

      const deltaMid = result ? newResult.mid - result.mid : null;
      if (deltaMid != null && deltaMid > 0) {
        setHeaderAnim('success');
        setHeaderAnimTrigger(Date.now());
        setTimeout(() => setHeaderAnim('hover'), 760);
      } else {
        setHeaderAnim('hover');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError({ code: 'unknown', message: String(err) }));
      setHeaderAnim('hover');
    } finally {
      setIsLoading(false);
    }
  }

  const clearForm = () => {
    if (confirm('Очистить форму и сбросить расчёт?')) {
      setForm(emptyForm());
      setResult(null);
      setPrevResult(null);
      setRecs([]);
      setLastSubmittedForm(null);
      setCurrentCalcId(null);
      setError(null);
      setHeaderAnim('hover');
    }
  };

  async function saveVersion() {
    if (!currentCalcId) return;
    try {
      await versionsApi.create({ calculation_id: currentCalcId });
      const list = await versionsApi.list();
      setVersions(list.map(versionToCard));
      showToast('Версия сохранена');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'version_conflict') {
        showToast('Эта версия уже сохранена');
        return;
      }
      showToast('Не удалось сохранить версию');
    }
  }

  const pickRec = (rec) => {
    setActiveRecId(rec.id);
    setTimeout(() => setActiveRecId(null), 700);

    const token = 'g_' + Date.now();
    if (rec.field === 'skills' && rec.payload) {
      if (!form.skills.includes(rec.payload)) {
        setForm({ ...form, skills: [...form.skills, rec.payload] });
      }
      setFieldGlow({ key: 'skills', token });
    } else if (rec.field === 'languages') {
      // The recommender doesn't pin a specific language — it just nudges
      // 'add a B2+ language'. We surface the block (expanded if any rows)
      // and leave the user to pick which language they actually speak.
      setFieldGlow({ key: 'languages', token });
    } else if (rec.field === 'remote' || rec.field === 'region') {
      setForm({ ...form, remote: true });
      setFieldGlow({ key: 'region', token });
    } else if (rec.field === 'education') {
      setFieldGlow({ key: 'education', token });
    } else if (rec.field === 'experience') {
      setFieldGlow({ key: 'experience', token });
    }
    setTimeout(() => {
      const el = document.querySelector(`#field-${rec.field === 'skills' ? 'skills' : rec.field}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top < 80 || rect.top > window.innerHeight - 80) {
          window.scrollTo({ top: window.scrollY + rect.top - 120, behavior: 'smooth' });
        }
      }
    }, 50);
    setTimeout(() => setFieldGlow(null), 800);
  };

  const restoreVersion = (v) => {
    setForm(JSON.parse(JSON.stringify(v.form)));
    setHistoryOpen(false);
    showToast('Версия восстановлена');
  };

  async function deleteVersion(id) {
    try {
      await versionsApi.remove(id);
      const list = await versionsApi.list();
      setVersions(list.map(versionToCard));
      showToast('Версия удалена');
    } catch { showToast('Не удалось удалить'); }
  }

  if (bootState === 'gated') {
    return <BackendGate onRecovered={() => { booted.current = false; boot(); }} />;
  }
  if (bootState !== 'ready' || !catalogs) {
    // Catalogs still loading. If it's the very first visit, the splash
    // doubles as a loading screen; otherwise show a minimal one.
    return splashShowing
      ? <Splash onDone={dismissSplash} />
      : <div className="splash"><div className="t-body t-muted">Загрузка…</div></div>;
  }

  return (
    <Fragment>
      {/* TODO(post-hackathon): pass onOpenLogin to <Landing> once login modal exists.
          Backend ready (see backend/app/api/auth.py). */}
      {screen === 'landing' && (
        <Landing
          onStart={goSplit}
          skillLabels={Object.fromEntries((catalogs?.skills || []).map((s) => [s.slug, s.name]))}
        />
      )}
      {screen === 'split' && (
        <SplitView
          catalogs={catalogs}
          form={form}
          setForm={setForm}
          result={result}
          prevResult={prevResult}
          recs={recs}
          onRecalc={recalc}
          onClear={clearForm}
          onSaveVersion={saveVersion}
          onPickRec={pickRec}
          activeRecId={activeRecId}
          onHome={goHome}
          onOpenHistory={() => setHistoryOpen(true)}
          hasHistory={versions.length}
          fieldGlow={fieldGlow}
          isLoading={isLoading}
          error={error}
          isEmpty={isEmpty}
          dirty={dirty}
          pulseToken={pulseToken}
          completion={completion}
          headerAnim={headerAnim}
          headerAnimTrigger={headerAnimTrigger}
        />
      )}

      <HistoryDrawer
        open={historyOpen}
        versions={versions}
        catalogs={catalogs}
        onClose={() => setHistoryOpen(false)}
        onRestore={restoreVersion}
        onDelete={deleteVersion}
      />

      <Toast visible={toast.visible} message={toast.message} />

      {splashShowing && <Splash onDone={dismissSplash} />}
    </Fragment>
  );
}


function calcCompletion(form) {
  let score = 0;
  if (form.role && form.role.trim().length > 1) score += 30;
  score += Math.min(35, form.skills.length * 7);
  score += 5;
  if (form.region) score += 10;
  const langs = form.languages || [];
  const HIGH = new Set(['b1', 'b2', 'c1', 'c2']);
  if (langs.some((l) => HIGH.has(l.level))) score += 10;
  if (form.education && form.education.trim().length > 1) score += 10;
  return Math.min(100, score);
}
